import React, { useEffect, useRef, useState } from "react";
import { useCart } from "../context/CartContext";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import { getShippingConfig, calcShipping } from "../services/shipping";
import { isMobileDevice, startMobileInicisPayment } from "../services/inicisMobile";
import AddressSearch from "../components/AddressSearch";
import { isValidKoreanMobile, formatKoreanMobile } from "../services/phone";
import { trackBeginCheckout } from "../services/analytics";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ─── Styles ─────────────────────────────────────────────────────────────────── */
const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

.gco-page {
  background: #f9f7f4;
  min-height: 100vh;
  padding: 40px 0 80px;
  font-family: 'DM Sans', sans-serif;
}
.gco-inner {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 20px;
}
.gco-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 28px;
}
.gco-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 24px;
  align-items: start;
}

/* ─── Cards ─── */
.gco-card {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8e4df;
  padding: 24px;
  margin-bottom: 20px;
}
.gco-card:last-child { margin-bottom: 0; }
.gco-card-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 18px;
}

/* ─── Inputs ─── */
.gco-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a5c38;
  margin-bottom: 6px;
}
.gco-input {
  width: 100%;
  border: 1.5px solid #e8e4df;
  border-radius: 11px;
  padding: 10px 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.9rem;
  color: #1a1a1a;
  background: #fff;
  outline: none;
  transition: border-color 0.18s;
  margin-bottom: 14px;
  box-sizing: border-box;
}
.gco-input:focus { border-color: #1a5c38; }
.gco-input::placeholder { color: #bbb; }
.gco-input.error { border-color: #d94f3d; }
textarea.gco-input { resize: vertical; min-height: 80px; }
.gco-input-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.gco-field { margin-bottom: 0; }
.gco-error-msg {
  font-size: 0.76rem;
  color: #d94f3d;
  margin-top: -10px;
  margin-bottom: 10px;
  display: block;
}

/* ─── Payment options ─── */
.gco-pay-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.gco-pay-option {
  border: 1.5px solid #e8e4df;
  border-radius: 12px;
  padding: 13px 16px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  display: flex;
  align-items: center;
  gap: 12px;
}
.gco-pay-option.selected { border-color: #1a5c38; background: #e8f3ed; }
.gco-pay-option.disabled { cursor: not-allowed; opacity: 0.7; background: #faf8f4; border-style: dashed; }
.gco-soon-badge { display: inline-block; margin-left: 8px; font-size: 0.66rem; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; color: #b45309; background: #fef3c7; border-radius: 999px; padding: 2px 8px; vertical-align: middle; }
.gco-pay-option input[type="radio"] { accent-color: #1a5c38; width: 16px; height: 16px; flex-shrink: 0; cursor: pointer; }
.gco-pay-option-label { font-size: 0.9rem; font-weight: 600; color: #1a1a1a; }
.gco-pay-option-desc  { font-size: 0.78rem; color: #8a8a8a; margin-top: 2px; }

/* ─── Payment detail boxes ─── */
.gco-pay-detail {
  border-radius: 12px;
  padding: 16px;
  margin-top: 14px;
  font-size: 0.87rem;
}
.gco-pay-detail.transfer { background: #f0f9f4; border: 1px solid #c5dece; }
.gco-pay-detail.card     { background: #f0f4ff; border: 1px solid #c5d0f0; }
.gco-pay-detail-title { font-weight: 700; color: #1a1a1a; margin-bottom: 10px; font-size: 0.9rem; }
.gco-bank-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  font-size: 0.86rem;
}
.gco-bank-row:last-of-type { border-bottom: none; }
.gco-bank-row span:first-child { color: #6a6a6a; }
.gco-bank-row span:last-child  { font-weight: 600; color: #1a1a1a; }
.gco-account-no { color: #1a5c38 !important; font-size: 1rem !important; }
.gco-info-box {
  margin-top: 10px;
  background: rgba(255,255,255,0.7);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.82rem;
  color: #4a4a4a;
  line-height: 1.5;
}
.gco-card-checks { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
.gco-card-check { display: flex; align-items: center; gap: 8px; font-size: 0.84rem; color: #4a4a4a; }
.gco-check-dot {
  width: 18px; height: 18px;
  background: #e8f3ed; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.65rem; color: #1a5c38; flex-shrink: 0;
}
.gco-whatsapp {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 11px;
  padding: 12px 14px;
  margin-top: 14px;
  font-size: 0.84rem;
  color: #4a4a4a;
}
.gco-whatsapp-icon { font-size: 1.4rem; flex-shrink: 0; margin-top: 1px; }

/* ─── Order summary (right card) ─── */
.gco-summary {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8e4df;
  padding: 24px;
  position: sticky;
  top: 88px;
}
.gco-summary-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 18px;
}
.gco-sum-loading {
  text-align: center;
  padding: 24px 0;
  color: #8a8a8a;
}
.gco-sum-spinner {
  width: 28px; height: 28px;
  border: 3px solid #e8e4df; border-top-color: #1a5c38;
  border-radius: 50%; animation: spin 0.8s linear infinite;
  margin: 0 auto 10px;
}
@keyframes spin { to { transform: rotate(360deg); } }
.gco-order-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f0ede9;
  gap: 10px;
}
.gco-order-item:last-of-type { border-bottom: none; }
.gco-item-name  { font-size: 0.84rem; color: #4a4a4a; flex: 1; line-height: 1.3; }
.gco-item-price { font-size: 0.84rem; font-weight: 600; color: #1a1a1a; white-space: nowrap; }
.gco-divider { height: 1px; background: #e8e4df; margin: 12px 0; }
.gco-sum-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  font-size: 0.88rem;
  color: #4a4a4a;
}
.gco-sum-row.total {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  border-top: 2px solid #e8e4df;
  padding-top: 12px;
  margin-top: 4px;
}
.gco-sum-row .free     { color: #1a5c38; font-weight: 600; }
.gco-sum-row .discount { color: #1a5c38; }

/* ─── Promo ─── */
.gco-promo-wrap { display: flex; gap: 8px; margin-top: 14px; }
.gco-promo-input {
  flex: 1;
  border: 1.5px solid #e8e4df; border-radius: 10px;
  padding: 9px 13px; font-family: 'DM Sans', sans-serif;
  font-size: 0.88rem; outline: none;
  transition: border-color 0.18s;
}
.gco-promo-input:focus { border-color: #1a5c38; }
.gco-promo-btn {
  background: #1a5c38; color: #fff;
  border: none; border-radius: 10px;
  padding: 9px 16px;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; white-space: nowrap; transition: background 0.15s;
}
.gco-promo-btn:hover { background: #134429; }
.gco-promo-applied {
  display: flex; align-items: center;
  justify-content: space-between;
  background: #e8f3ed; border-radius: 10px;
  padding: 10px 14px; margin-top: 14px; font-size: 0.87rem;
}
.gco-promo-applied span { color: #1a5c38; font-weight: 600; }
.gco-promo-remove {
  background: none; border: none; color: #d94f3d;
  font-size: 0.82rem; cursor: pointer; font-weight: 600;
  font-family: 'DM Sans', sans-serif;
}
.gco-promo-remove:hover { opacity: 0.7; }

/* ─── Place order button ─── */
.gco-place-btn {
  width: 100%; margin-top: 20px;
  background: #1a5c38; color: #fff;
  border: none; border-radius: 13px; padding: 15px;
  font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 700;
  cursor: pointer; transition: background 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.gco-place-btn:hover:not(:disabled) { background: #134429; }
.gco-place-btn:disabled { background: #a0c4b0; cursor: not-allowed; }
.gco-spin { animation: spin 1s linear infinite; display: inline-block; }

/* ─── Responsive ─── */
@media (max-width: 900px) {
  .gco-grid { grid-template-columns: 1fr; }
  .gco-summary { position: static; }
}
@media (max-width: 520px) {
  .gco-inner { padding: 0 14px; }
  .gco-title  { font-size: 1.5rem; }
  .gco-input-row { grid-template-columns: 1fr; gap: 0; }
}
`;

/* ─── Main Component ─────────────────────────────────────────────────────────── */
const CheckoutPage = () => {
  const { cartItems, isLoading } = useCart();
  const { t } = useTranslation();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [payData,        setPayData]        = useState(null);
  const [promoCode,      setPromoCode]      = useState("");
  const [promoApplied,   setPromoApplied]   = useState(false);
  const [promoDiscount,  setPromoDiscount]  = useState(0);
  const [shippingPrice,  setShippingPrice]  = useState(0);
  const [subtotal,       setSubtotal]       = useState(0);
  const [total,          setTotal]          = useState(0);
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [paymentMethod,  setPaymentMethod]  = useState("account_transfer");
  const [depositorName,  setDepositorName]  = useState("");
  const [payMethods,     setPayMethods]     = useState({ card_enabled: false, transfer_enabled: true });
  const [productDetails, setProductDetails] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [formErrors,     setFormErrors]     = useState({});

  const [billing, setBilling] = useState({
    firstName: "", lastName: "", phone: "", email: "", notes: "",
  });

  // Korean address, filled by the postcode search (never typed free-hand).
  const [address, setAddress] = useState({
    postal_code: "", road_address: "", address_line: "", address_details: "",
    sido: "", sigungu: "", bname: "", building_name: "", address_english: "",
    address_source: "manual", country: "South Korea",
  });

  const handleAddressChange = (patch) => {
    setAddress(prev => ({ ...prev, ...patch }));
    setFormErrors(prev => ({ ...prev, address: "", address_details: "" }));
  };

  // ─── INIStdPay check ──────────────────────────────────────────────────────
  useEffect(() => {
    setTimeout(() => {
      if (typeof window.INIStdPay !== "undefined") console.log("INIStdPay loaded");
      else console.warn("INIStdPay not loaded yet");
    }, 1000);
  }, []);

  // ─── Payment errors from redirect ─────────────────────────────────────────
  useEffect(() => {
    if (location.state?.paymentError) {
      toast.error(location.state.paymentError);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // ─── Redirect if cart empty ────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoading && cartItems.length === 0) {
      toast.error(t("checkout.cartEmpty"));
      navigate("/cart");
    }
  }, [cartItems, isLoading, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const [shipCfg, setShipCfg] = useState({ fee: 5000, threshold: 50000 });
  useEffect(() => { getShippingConfig().then(setShipCfg); }, []);

  // Which payment methods the super-admin currently allows (card defaults off).
  useEffect(() => {
    api.get("/settings/payment-methods")
      .then((r) => {
        const cfg = { card_enabled: !!r.data.card_enabled, transfer_enabled: !!r.data.transfer_enabled };
        setPayMethods(cfg);
        setPaymentMethod((cur) =>
          (cur === "inicis_card" && !cfg.card_enabled) ? "account_transfer"
          : (cur === "account_transfer" && !cfg.transfer_enabled && cfg.card_enabled) ? "inicis_card"
          : cur
        );
      })
      .catch(() => {});
  }, []);

  const methodEnabled = (v) =>
    v === "inicis_card" ? payMethods.card_enabled
    : v === "account_transfer" ? payMethods.transfer_enabled
    : true;

  // ─── Fetch product details & compute totals ────────────────────────────────
  useEffect(() => {
    const fetchAndCalculate = async () => {
      if (cartItems.length === 0) return;
      setLoadingSummary(true);
      try {
        let calc = 0;
        const details = {};
        for (const item of cartItems) {
          const productId = item.product_id?._id || item.product_id;
          if (productId) {
            try {
              const res     = await api.get(`/products/${productId}`);
              const product = res.data;
              details[productId] = product;
              const price = product?.is_on_sale ? product?.sale_price : product?.sell_price;
              calc += (price || 0) * item.quantity;
            } catch {
              details[productId] = { name: "Unknown Product", sell_price: 0, sale_price: 0, is_on_sale: false };
            }
          }
        }
        const ship = calcShipping(calc, shipCfg);
        setProductDetails(details);
        setSubtotal(calc);
        setShippingPrice(ship);
        setTotal(calc - promoDiscount + ship);
      } catch {
        toast.error(t("checkout.calcFailed"));
      } finally {
        setLoadingSummary(false);
      }
    };
    fetchAndCalculate();
  }, [cartItems, promoDiscount, shipCfg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire begin_checkout exactly once per visit, the first time totals are
  // ready — not on every recalc (promo code, shipping) or it'd fire repeatedly.
  const beganCheckoutRef = useRef(false);
  useEffect(() => {
    if (!beganCheckoutRef.current && !loadingSummary && cartItems.length > 0 && total > 0) {
      beganCheckoutRef.current = true;
      trackBeginCheckout(cartItems, total);
    }
  }, [loadingSummary, cartItems, total]);

  // ─── Promo ─────────────────────────────────────────────────────────────────
  const handlePromoCheck = async () => {
    if (!promoCode.trim()) { toast.error(t("checkout.enterPromo")); return; }
    try {
      // Validate against the real endpoint (not the create route) and pass the
      // order total so min-order codes are checked correctly.
      const res   = await api.post("/promos/validate", { code: promoCode.trim(), order_total: subtotal });
      const promo = res.data;
      if (promo.valid) {
        // This page subtracts a flat amount, so use the backend's pre-computed
        // discount (already correct for both percent and fixed codes).
        setPromoDiscount(promo.discount);
        setPromoApplied(true);
        toast.success(`Promo applied: ₩${Number(promo.discount).toLocaleString()} off`);
      } else {
        setPromoDiscount(0); setPromoApplied(false);
        toast.error(promo.message || "Invalid promo code");
      }
    } catch (err) {
      // Show the backend's specific reason (expired / inactive / min order / …).
      setPromoDiscount(0); setPromoApplied(false);
      toast.error(err.response?.data?.message || "Failed to validate promo code");
    }
  };

  const removePromoCode = () => {
    setPromoCode(""); setPromoDiscount(0); setPromoApplied(false);
    toast.info(t("checkout.promoRemoved"));
  };

  // ─── Form validation ───────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!billing.firstName.trim()) errors.firstName = "First name is required";
    if (!billing.lastName.trim())  errors.lastName  = "Last name is required";
    if (!address.postal_code || !(address.road_address || address.address_line))
      errors.address = t("address.required");
    if (!address.address_details?.trim())
      errors.address_details = t("address.detailRequired");
    if (!billing.phone.trim())     errors.phone     = "Phone is required";
    if (!billing.email.trim())     errors.email     = "Email is required";
    if (billing.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email))
      errors.email = "Please enter a valid email";
    // The courier calls this — a landline or a typo means an undelivered parcel.
    if (billing.phone && !isValidKoreanMobile(billing.phone))
      errors.phone = t("auth.invalidPhone");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setBilling(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: "" }));
  };

  // ─── INIpay ────────────────────────────────────────────────────────────────
  const buildGoodName = () => {
    const names = cartItems
      .map(item => { const id = item.product_id?._id || item.product_id; return productDetails[id]?.name || "Product"; })
      .slice(0, 2).join(", ");
    return cartItems.length > 2 ? `${names} 외 ${cartItems.length - 2}건` : names;
  };

  const prepareINIpayData = async () => {
    const res = await api.get(`/inicis/prepare?price=${total}`);
    return {
      ...res.data,
      goodname:   buildGoodName(),
      buyername:  `${billing.firstName} ${billing.lastName}`.trim(),
      buyertel:   billing.phone.replace(/[^0-9]/g, ""),
      buyeremail: billing.email,
    };
  };

  const handleINIpayPayment = async () => {
    if (!payMethods.card_enabled) { toast.info(t("checkout.cardComingSoon")); return; }
    if (!validateForm()) { toast.error(t("checkout.fillRequired")); return; }
    if (cartItems.length === 0) { toast.error(t("checkout.cartEmpty")); return; }
    if (!isMobileDevice() && typeof window.INIStdPay === "undefined") { toast.error(t("checkout.paymentModuleError")); return; }
    setIsSubmitting(true);
    try {
      const params = await prepareINIpayData();
      sessionStorage.setItem("pendingOrder", JSON.stringify({
        firstName: billing.firstName, lastName: billing.lastName,
        email: billing.email, phone: billing.phone,
        address, notes: billing.notes,
        promoCode: promoApplied ? promoCode : null,
        discount: promoDiscount, shipping: shippingPrice, subtotal, total,
        cartItems: cartItems.map(item => {
          const productId = item.product_id?._id || item.product_id;
          const product   = productDetails[productId] || {};
          return { productId, productName: product.name || "Product", quantity: item.quantity,
            price: product.is_on_sale ? product.sale_price : product.sell_price || 0 };
        }),
      }));
      // Phones can't use the PC INIStdPay window — go through the Inicis
      // mobile flow instead (full-page redirect; returns on /payment/success).
      if (isMobileDevice()) {
        startMobileInicisPayment(params);
        return;
      }
      setPayData(params);
      setTimeout(() => { window.INIStdPay.pay("SendPayForm_id"); setIsSubmitting(false); }, 100);
    } catch {
      toast.error(t("checkout.paymentInitFailed"));
      setIsSubmitting(false);
    }
  };

  // ─── Traditional order ─────────────────────────────────────────────────────
  const handleTraditionalOrder = async () => {
    if (!validateForm()) { toast.error(t("checkout.fillRequired")); return; }
    if (paymentMethod === "account_transfer" && !depositorName.trim()) {
      toast.error(t("checkout.depositorRequired")); return;
    }
    if (cartItems.length === 0) { toast.error(t("checkout.cartEmpty")); return; }
    setIsSubmitting(true);
    try {
      const guestToken = localStorage.getItem("authToken");
      if (!guestToken) throw new Error("Authentication token not found");
      const response = await api.post("/orders/guest-checkout", {
        guest_token: guestToken,
        name:  `${billing.firstName} ${billing.lastName}`,
        email: billing.email, phone: billing.phone,
        address: {
          ...address,
          address_type: "shipping",
          full_name: `${billing.firstName} ${billing.lastName}`,
          is_default_billing: false, is_default_shipping: false,
          phone: billing.phone,
        },
        payment_method: paymentMethod, depositor_name: depositorName,
        shipping_price: shippingPrice, discount_price: promoDiscount,
        note: billing.notes, promo_code: promoApplied ? promoCode : null,
        auto_create_transaction: true, subtotal, total,
      });
      if (response.data) {
        toast.success(t("checkout.orderPlaced"));
        navigate("/order-success", {
          state: {
            orderId: response.data.order?._id || response.data._id,          // Mongo _id (upload-proof)
            orderNumber: response.data.order_id || response.data.order?.order_id, // FM-YYYY-MM-DD-#### (display)
            total,
            orderTotal: total,
            paymentMethod,
          },
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || "Order failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOrder = () => {
    if (paymentMethod === "inicis_card") handleINIpayPayment();
    else handleTraditionalOrder();
  };

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="gco-page">
        <div className="gco-inner" style={{ textAlign: "center", paddingTop: "80px" }}>
          <div style={{ width: 36, height: 36, border: "3px solid #e8e4df", borderTopColor: "#1a5c38", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: "#8a8a8a", fontFamily: "'DM Sans', sans-serif" }}>{t("checkout.loadingCheckout")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{S}</style>
      <div className="gco-page">
        <ToastContainer position="bottom-right" autoClose={3000} />

        {/* ─── INIpay hidden form ─── */}
        {payData && (
          <form id="SendPayForm_id" method="post" style={{ display: "none" }}>
            <input type="hidden" name="version"      value="1.0" />
            <input type="hidden" name="mid"          value={payData.mid} />
            <input type="hidden" name="oid"          value={payData.oid} />
            <input type="hidden" name="price"        value={payData.price} />
            <input type="hidden" name="timestamp"    value={payData.timestamp} />
            <input type="hidden" name="use_chkfake"  value={payData.use_chkfake ?? "Y"} />
            <input type="hidden" name="signature"    value={payData.signature} />
            <input type="hidden" name="verification" value={payData.verification} />
            <input type="hidden" name="mKey"         value={payData.mKey} />
            <input type="hidden" name="currency"     value="WON" />
            <input type="hidden" name="gopaymethod"  value="Card:DirectBank:HPP:KakaoPay" />
            <input type="hidden" name="goodname"     value={payData.goodname ?? buildGoodName()} />
            <input type="hidden" name="buyername"    value={payData.buyername ?? `${billing.firstName} ${billing.lastName}`.trim()} />
            <input type="hidden" name="buyertel"     value={payData.buyertel ?? billing.phone.replace(/[^0-9]/g, "")} />
            <input type="hidden" name="buyeremail"   value={payData.buyeremail ?? billing.email} />
            <input type="hidden" name="acceptmethod" value={payData.acceptmethod ?? "HPP(1):va_receipt:below1000:centerCd(Y)"} />
            <input type="hidden" name="returnUrl"    value={`${window.location.origin}/api/inicis/return`} />
            <input type="hidden" name="closeUrl"     value={`${window.location.origin}/close.html`} />
          </form>
        )}

        <div className="gco-inner">
          <h1 className="gco-title">{t("checkout.title")}</h1>

          <div className="gco-grid">
            {/* ─── LEFT ─── */}
            <div>

              {/* Shipping Details */}
              <div className="gco-card">
                <div className="gco-card-title">📦 {t("checkout.shippingDetails")}</div>

                <div className="gco-input-row">
                  <div className="gco-field">
                    <span className="gco-label">{t("auth.firstName")} *</span>
                    <input className={`gco-input ${formErrors.firstName ? "error" : ""}`}
                      type="text" value={billing.firstName} placeholder={t("auth.firstName")}
                      onChange={e => handleInputChange("firstName", e.target.value)} />
                    {formErrors.firstName && <span className="gco-error-msg">{formErrors.firstName}</span>}
                  </div>
                  <div className="gco-field">
                    <span className="gco-label">{t("auth.lastName")} *</span>
                    <input className={`gco-input ${formErrors.lastName ? "error" : ""}`}
                      type="text" value={billing.lastName} placeholder={t("auth.lastName")}
                      onChange={e => handleInputChange("lastName", e.target.value)} />
                    {formErrors.lastName && <span className="gco-error-msg">{formErrors.lastName}</span>}
                  </div>
                </div>

                <AddressSearch
                  value={address}
                  onChange={handleAddressChange}
                  error={formErrors.address}
                  detailError={formErrors.address_details}
                />

                <div className="gco-input-row">
                  <div className="gco-field">
                    <span className="gco-label">{t("checkout.phone")} *</span>
                    <input className={`gco-input ${formErrors.phone ? "error" : ""}`}
                      type="tel" value={billing.phone} placeholder="010-1234-5678"
                      inputMode="numeric" maxLength={13}
                      onChange={e => handleInputChange("phone", formatKoreanMobile(e.target.value))} />
                    {formErrors.phone && <span className="gco-error-msg">{formErrors.phone}</span>}
                  </div>
                  <div className="gco-field">
                    <span className="gco-label">{t("auth.email")} *</span>
                    <input className={`gco-input ${formErrors.email ? "error" : ""}`}
                      type="email" value={billing.email} placeholder="your@email.com"
                      onChange={e => handleInputChange("email", e.target.value)} />
                    {formErrors.email && <span className="gco-error-msg">{formErrors.email}</span>}
                  </div>
                </div>

                <span className="gco-label">{t("checkout.specialInstructions")}</span>
                <textarea className="gco-input" value={billing.notes} rows={3}
                  placeholder={t("checkout.instructionsPlaceholder")}
                  onChange={e => handleInputChange("notes", e.target.value)} />
              </div>

              {/* Payment Method */}
              <div className="gco-card">
                <div className="gco-card-title">💳 {t("success.paymentMethod")}</div>

                <div className="gco-pay-options">
                  {[
                    { value: "account_transfer", emoji: "🏦", label: t("success.accountTransfer"), desc: t("checkout.transferDesc") },
                    { value: "inicis_card",      emoji: "💳", label: t("success.cardPayment"),     desc: t("checkout.cardDesc") },
                  ].filter(opt => methodEnabled(opt.value) || opt.value === "inicis_card").map(opt => {
                    const enabled = methodEnabled(opt.value);
                    return (
                      <label key={opt.value}
                        className={`gco-pay-option ${paymentMethod === opt.value ? "selected" : ""} ${enabled ? "" : "disabled"}`}
                        onClick={(e) => { if (!enabled) { e.preventDefault(); toast.info(t("checkout.cardComingSoon")); } }}>
                        <input type="radio" name="payment" value={opt.value}
                          checked={paymentMethod === opt.value}
                          disabled={!enabled}
                          onChange={() => enabled && setPaymentMethod(opt.value)} />
                        <div>
                          <div className="gco-pay-option-label">
                            {opt.emoji} {opt.label}
                            {!enabled && <span className="gco-soon-badge">{t("checkout.comingSoon")}</span>}
                          </div>
                          <div className="gco-pay-option-desc">
                            {enabled ? opt.desc : t("checkout.cardComingSoonDesc")}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Account Transfer Detail */}
                {paymentMethod === "account_transfer" && (
                  <div className="gco-pay-detail transfer">
                    <div className="gco-pay-detail-title">🏦 {t("checkout.bankDetails")}</div>
                    <div className="gco-bank-row"><span>{t("checkout.bank")}</span><span>NH 농협은행 (NH Bank)</span></div>
                    <div className="gco-bank-row"><span>{t("checkout.accountTitle")}</span><span>ABBAS AZHAR (패밀리월드마트)</span></div>
                    <div className="gco-bank-row"><span>{t("checkout.accountNo")}</span><span className="gco-account-no">355-0084-6104-43</span></div>
                    <div style={{ marginTop: "14px" }}>
                      <span className="gco-label">{t("checkout.depositorName")} *</span>
                      <input className="gco-input" value={depositorName}
                        onChange={e => setDepositorName(e.target.value)}
                        placeholder={t("checkout.depositorPlaceholder")} />
                    </div>
                    <div className="gco-info-box">
                      {t("checkout.exactNameNote")}
                    </div>
                    <div className="gco-whatsapp" style={{ marginTop: "10px" }}>
                      <span className="gco-whatsapp-icon">💬</span>
                      <div>
                        <strong>{t("checkout.whatsappTitle")}</strong>
                        <div style={{ marginTop: "3px", color: "#6a6a6a", fontSize: "0.82rem" }}>
                          {t("checkout.whatsappNote")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* INIpay Card Detail */}
                {paymentMethod === "inicis_card" && (
                  <div className="gco-pay-detail card">
                    <div className="gco-pay-detail-title">🔒 {t("checkout.secureCardTitle")}</div>
                    <div className="gco-card-checks">
                      {[t("checkout.check1"), t("checkout.check2"),
                        t("checkout.check3"), t("checkout.check4")].map((c, i) => (
                        <div className="gco-card-check" key={i}>
                          <div className="gco-check-dot">✓</div>
                          <span>{c}</span>
                        </div>
                      ))}
                    </div>
                    <div className="gco-info-box">
                      Click "Place Order" → INIpay popup opens → Complete payment → Return to order confirmation.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ─── RIGHT: Summary ─── */}
            <div className="gco-summary">
              <div className="gco-summary-title">{t("cart.orderSummary")}</div>

              {loadingSummary ? (
                <div className="gco-sum-loading">
                  <div className="gco-sum-spinner" />
                  <span>{t("checkout.calculating")}</span>
                </div>
              ) : (
                <>
                  {cartItems.map((item, index) => {
                    const productId = item.product_id?._id || item.product_id;
                    const product   = productDetails[productId] || {};
                    const price     = product.is_on_sale ? product.sale_price : product.sell_price || 0;
                    return (
                      <div className="gco-order-item" key={index}>
                        <span className="gco-item-name">{product.name || "Product"} × {item.quantity}</span>
                        <span className="gco-item-price">₩{(price * item.quantity).toLocaleString()}</span>
                      </div>
                    );
                  })}

                  <div className="gco-divider" />

                  <div className="gco-sum-row">
                    <span>{t("cart.subtotal")}</span>
                    <span>₩{subtotal.toLocaleString()}</span>
                  </div>
                  {promoApplied && (
                    <div className="gco-sum-row">
                      <span>{t("cart.discount")} ({promoCode})</span>
                      <span className="discount">-₩{promoDiscount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="gco-sum-row">
                    <span>{t("cart.shipping")}</span>
                    <span>{shippingPrice === 0 ? <span className="free">{t("cart.free")}</span> : `₩${shippingPrice.toLocaleString()}`}</span>
                  </div>
                  <div className="gco-sum-row total">
                    <span>{t("cart.total")}</span>
                    <span>₩{total.toLocaleString()}</span>
                  </div>
                </>
              )}

              {/* Promo */}
              {!promoApplied ? (
                <div className="gco-promo-wrap">
                  <input className="gco-promo-input" type="text"
                    placeholder={t("checkout.promoPlaceholder")} value={promoCode}
                    onChange={e => setPromoCode(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handlePromoCheck(); }} />
                  <button className="gco-promo-btn" onClick={handlePromoCheck}>{t("checkout.apply")}</button>
                </div>
              ) : (
                <div className="gco-promo-applied">
                  <span>🎉 {t("checkout.promoApplied", { code: promoCode })}</span>
                  <button className="gco-promo-remove" onClick={removePromoCode}>{t("cart.remove")}</button>
                </div>
              )}

              {/* Place Order */}
              <button
                className="gco-place-btn"
                onClick={handleOrder}
                disabled={loadingSummary || isSubmitting || cartItems.length === 0}
              >
                {isSubmitting
                  ? <><span className="gco-spin">⟳</span> {t("checkout.processing")}</>
                  : t("checkout.placeOrderBtn", { total: total.toLocaleString() })
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;